import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { nibApi } from '@services/api';
import { useBusinessProfile } from '@/hooks/business/useBusinessProfile';
import type {
  NIBSubmissionCreateRequest,
  NIBFormatType,
  NIBPayIndicator,
} from '@/types';
import { NIB_PAY_INDICATOR_LABELS } from '@/types';

interface SubmissionFormData {
  employer_number: string;
  employer_name: string;
  contribution_month: string;
  contribution_year: string;
  format_type: NIBFormatType;
  pay_indicator: NIBPayIndicator;
}

// Generate month options (January - December)
const monthOptions = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Generate year options (current year + 2 years ahead, 5 years back)
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years: Array<{ value: string; label: string }> = [];

  for (let i = currentYear + 2; i >= currentYear - 5; i--) {
    years.push({
      value: i.toString(),
      label: i.toString(),
    });
  }

  return years;
};

const yearOptions = generateYearOptions();

// Pay indicator options
const payIndicatorOptions: Array<{
  value: NIBPayIndicator;
  label: string;
}> = [
  { value: 'W', label: NIB_PAY_INDICATOR_LABELS.W },
  { value: 'B', label: NIB_PAY_INDICATOR_LABELS.B },
  { value: 'S', label: NIB_PAY_INDICATOR_LABELS.S },
  { value: 'M', label: NIB_PAY_INDICATOR_LABELS.M },
];

export default function NIBSubmissionNew() {
  const navigate = useNavigate();
  const { data: profile, isLoading: profileLoading } = useBusinessProfile();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SubmissionFormData>({
    defaultValues: {
      employer_number: '',
      employer_name: '',
      contribution_month: new Date().getMonth().toString().padStart(2, '0'),
      contribution_year: new Date().getFullYear().toString(),
      format_type: 'NON_HOSPITALITY',
      pay_indicator: 'W',
    },
  });

  // Auto-populate form when business profile loads
  useEffect(() => {
    if (profile) {
      reset({
        employer_number: profile.employer_ni_number,
        employer_name: profile.company_name,
        contribution_month: new Date().getMonth().toString().padStart(2, '0'),
        contribution_year: new Date().getFullYear().toString(),
        format_type: profile.format_type || 'NON_HOSPITALITY',
        pay_indicator: 'W',
      });
    }
  }, [profile, reset]);

  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const onSubmit = async (data: SubmissionFormData) => {
    try {
      setIsSubmitting(true);

      // Construct contribution_period in MMYY format
      const contributionPeriod = `${data.contribution_month}${data.contribution_year.slice(-2)}`;

      const requestData: NIBSubmissionCreateRequest = {
        employer_number: data.employer_number,
        employer_name: data.employer_name || undefined,
        contribution_period: contributionPeriod,
        format_type: data.format_type,
        pay_indicator: data.pay_indicator,
      };

      const newSubmission = await nibApi.createSubmission(requestData);
      if (newSubmission.total_records > 0) {
        toast.success(
          `Submission created with ${newSubmission.total_records} employees from roster`
        );
      } else {
        toast.success('NIB submission created successfully');
      }

      setHasUnsavedChanges(false);
      navigate(`/nib-c10/submissions/${newSubmission.id}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create submission';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (
      hasUnsavedChanges &&
      !window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      )
    ) {
      return;
    }
    navigate('/nib-c10/submissions');
  };

  // Show loading state while fetching profile
  if (profileLoading) {
    return (
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show warning if no business profile
  if (!profile) {
    return (
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">
              Business Profile Required
            </h3>
            <p className="text-yellow-700 mb-4">
              Please complete your business profile setup before creating NIB
              submissions.
            </p>
            <Link
              to="/settings?tab=business"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Go to Business Settings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Submissions
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Create New NIB C10 Submission
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Set up a new NIB C10 contribution submission for processing
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Employer Information (from Business Profile) */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Employer Information
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Information from your business profile
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">NIB Employer Number:</span>{' '}
                  <span className="font-medium text-gray-900">
                    {profile.employer_ni_number}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Company Name:</span>{' '}
                  <span className="font-medium text-gray-900">
                    {profile.company_name}
                  </span>
                </div>
                {profile.trading_name && (
                  <div>
                    <span className="text-gray-500">Trading Name:</span>{' '}
                    <span className="font-medium text-gray-900">
                      {profile.trading_name}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Format Type:</span>{' '}
                  <span className="font-medium text-gray-900">
                    {profile.format_type === 'HOSPITALITY'
                      ? 'Hospitality (115-byte)'
                      : 'Non-Hospitality (79-byte)'}
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-500">
              To change your employer information,{' '}
              <Link
                to="/settings?tab=business"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                update your business profile
              </Link>
            </p>
          </div>

          {/* Contribution Period */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Contribution Period
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Month */}
              <div>
                <label
                  htmlFor="contribution_month"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Month <span className="text-red-500">*</span>
                </label>
                <select
                  id="contribution_month"
                  {...register('contribution_month', {
                    required: 'Month is required',
                  })}
                  className={`w-full rounded-md shadow-sm ${
                    errors.contribution_month
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                >
                  {monthOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.contribution_month && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.contribution_month.message}
                  </p>
                )}
              </div>

              {/* Year */}
              <div>
                <label
                  htmlFor="contribution_year"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Year <span className="text-red-500">*</span>
                </label>
                <select
                  id="contribution_year"
                  {...register('contribution_year', {
                    required: 'Year is required',
                  })}
                  className={`w-full rounded-md shadow-sm ${
                    errors.contribution_year
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                >
                  {yearOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.contribution_year && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.contribution_year.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pay Indicator */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Payroll Settings
            </h2>

            <div>
              <label
                htmlFor="pay_indicator"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Pay Frequency
              </label>
              <select
                id="pay_indicator"
                {...register('pay_indicator')}
                className="w-full md:w-1/2 rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              >
                {payIndicatorOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                How often employees are paid
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 bg-white shadow rounded-lg p-6">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Creating...' : 'Create Submission'}
            </button>
          </div>
        </form>

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <div className="fixed bottom-4 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-800">
                  You have unsaved changes
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
